// @flow
import BaseModel from "../base/BaseModel";
import ChoiceAttributeOptionCollection from "./ChoiceAttributeOptionCollection";

import { has } from "../../utils/helpers/objects";
import { TIMEVERSION_FILTER_NAME } from "../../constants/Constants";
import { TITLE } from "../../constants/LayoutHints";
import { hasAllContentInData } from "../../constants/Settings";

import LinkCollection from "../links/LinkCollection";
import AttributeCollection from "./AttributeCollection";
import ConceptDetailModel from "../concepts/ConceptDetailModel";
import AttributeContent from "./AttributeContent";

import type {
  IModelWithChildModels,
  ModelOptions,
  ModularUIModel,
} from "../types";
import type ListItemModel from "../list/ListItemModel";
import type LinkModel from "../links/LinkModel";
import type ErrorResponse from "../error/ErrorResponse";

export type ChoiceAttributeOptionModelOptions = {
  ...ModelOptions,
  referenceDate: ISO_DATE,
};

/**
 */
class ChoiceAttributeOptionModel
  extends BaseModel
  implements IModelWithChildModels
{
  _code: string;
  _isSelected: boolean;
  _referenceDate: ISO_DATE;
  _level: number;
  _children: ChoiceAttributeOptionCollection;
  _concept: ?ConceptDetailModel;
  _links: LinkCollection;
  _attributeCollection: AttributeCollection;
  _content: AttributeContent;
  _isUser: boolean = false;

  /**
   */
  constructor(
    selectedValues: Array<string> = [],
    option: Object = {},
    modelOptions?: ChoiceAttributeOptionModelOptions,
  ) {
    super({}, option);

    this.code = this.contributions.code ?? this.contributions.key ?? "";

    this._isSelected = selectedValues.includes(this.code);

    this._referenceDate =
      modelOptions?.referenceDate || new Date().toISOString().split("T")[0];

    this._level = 0;

    this._attributeCollection = this.createAttributeCollection(modelOptions);
    this._children = this.addChildren(selectedValues, option, modelOptions);

    this._links = new LinkCollection(
      this.contributions._links,
      {},
      { origin: modelOptions?.origin, contextPath: modelOptions?.contextPath },
    );

    this._content = new AttributeContent(option.content);

    this._isUser = option.optionType === "user";
  }

  /**
   */
  static createFromListItemModel(
    listitem: ListItemModel,
  ): ChoiceAttributeOptionModel {
    const option = new ChoiceAttributeOptionModel();

    option.code = listitem.id.toString();
    option.attributeCollection = listitem.attributeCollection.clone();
    option.links = listitem.links.clone();

    return option;
  }

  /**
   * Indicates that this a choice attribute option model, used when adding options to a choice attribute
   * @returns {boolean}
   */
  get isChoiceAttributeOptionModel(): boolean {
    return true;
  }

  /**
   */
  getInitialChildModelLinks(): Array<LinkModel> {
    const initialLinks = [];

    if (!this.hasContentFromData && this.conceptLink !== null) {
      initialLinks.push(this.conceptLink);
    }

    if (this.children) {
      initialLinks.push(...this.children.getInitialChildModelLinks());
    }

    return initialLinks;
  }

  /**
   */
  setChildModels(models: Array<ModularUIModel>, errors: Array<ErrorResponse>) {
    if (this.conceptLink !== null) {
      const conceptHref = this.conceptLink.href;

      this.concept = models.find((model) =>
        model.selfhref.equalsWithParameters(conceptHref),
      );
    }

    if (this.children) {
      this.children.setChildModels(models, errors);
    }
  }

  /**
   * Get code of option
   */
  get code(): string {
    return this._code;
  }

  /**
   */
  set code(code: string) {
    this._code = code;
  }

  /**
   */
  equals(model: ChoiceAttributeOptionModel): boolean {
    return this.code.toString() === model.code.toString();
  }

  /**
   * Retrieve attributes for Composite codemaps (e.g. table and case list codemaps)
   */
  get attributeCollection(): AttributeCollection {
    return this._attributeCollection;
  }

  /**
   */
  set attributeCollection(attributeCollection: AttributeCollection) {
    this._attributeCollection = attributeCollection;
  }

  /**
   */
  createAttributeCollection(
    modelOptions?: ChoiceAttributeOptionModelOptions,
  ): AttributeCollection {
    const contributions = this.getContribution("elementsContributions", []);

    if (has(this.contributions, "elements")) {
      const data = this.getContribution("elements", {});
      return new AttributeCollection(data, contributions, {
        origin: modelOptions?.origin,
        contextPath: modelOptions?.contextPath,
        isReadonly: true,
      });
    }

    return new AttributeCollection();
  }

  /**
   * Get label of option for simple codemaps (e.g. static codemap)
   */
  get label(): string {
    if (this.contributions.label) {
      return this.contributions.label;
    }

    if (this.attributeCollection.hasItems) {
      const title = this.attributeCollection.getAttributeByLayoutHint(TITLE);

      if (title) {
        return title.readonlyvalue;
      }

      return this.attributeCollection.all
        .map((attribute) => attribute.readonlyvalue)
        .join(" · ");
    }

    return "";
  }

  /**
   * Retrieve the first permitted label to render when a concept and contentConfiguration is available
   * Be aware that permission could be in place for labels from a concept.
   */
  getContentConfiguredLabel(contentConfiguration: Object): string {
    const configuredLabelProperties =
      contentConfiguration?.labelConfig?.[0]?.types ?? [];

    if (
      !this.hasContentFromData &&
      this.concept &&
      configuredLabelProperties.length > 0
    ) {
      const configuredLabels = this.concept
        .getLabelElementByIds(configuredLabelProperties)
        .filter(
          (configuredLabel) =>
            configuredLabel.value && configuredLabel.value !== "",
        );

      if (configuredLabels.length > 0) {
        return configuredLabelProperties
          .filter((configuredLabelProperty) =>
            configuredLabels.some(
              (configuredLabel) =>
                configuredLabel._id === configuredLabelProperty,
            ),
          )
          .map((configuredLabelProperty) =>
            configuredLabels.find(
              (configuredLabel) =>
                configuredLabel._id === configuredLabelProperty,
            ),
          )[0].value;
      }
    }

    return this.label;
  }

  /**
   * Indicates if option is selected
   */
  get selected(): boolean {
    return this._isSelected;
  }

  /**
   * Set selected property of option
   */
  set selected(selected: boolean) {
    this._isSelected = selected;
  }

  /**
   * Retrieve count of filter for option
   */
  get count(): number | null {
    return this.getContribution("count");
  }

  /**
   */
  addChildren(
    selectedValues: Array<string> = [],
    option: Object = {},
    modelOptions?: ChoiceAttributeOptionModelOptions,
  ): ChoiceAttributeOptionCollection {
    // When contributions contain elements this is a list not nested options
    if (has(option, "elements")) {
      return new ChoiceAttributeOptionCollection(modelOptions);
    }

    return ChoiceAttributeOptionCollection.create(
      {
        value: selectedValues,
      },
      { options: option?.children ?? [] },
      modelOptions,
    );
  }

  /**
   * get children of option
   */
  get children(): ChoiceAttributeOptionCollection {
    return this._children;
  }

  /**
   * Retrieve links of attribute
   */
  get links(): LinkCollection {
    return this._links;
  }

  /**
   */
  set links(links: LinkCollection) {
    this._links = links;
  }

  /**
   * referenceDate received from attribute
   */
  get referenceDate(): ISO_DATE {
    return this._referenceDate;
  }

  /**
   */
  set referenceDate(date: ISO_DATE) {
    this._referenceDate = date;
  }

  /**
   * Retrieve concept link of attribute when available
   */
  get conceptLink(): LinkModel | null {
    const conceptLink = this.links.getLinkByKey("concept");
    if (conceptLink !== null) {
      conceptLink.href = conceptLink.href.addParameter(
        TIMEVERSION_FILTER_NAME,
        this.referenceDate,
      );

      conceptLink.isCacheable = true;
    }

    return conceptLink;
  }

  /**
   *
   */
  get avatarLink(): LinkModel | null {
    return this.links.getLinkByKey("avatar");
  }

  /**
   * Get concept information
   */
  get concept(): ConceptDetailModel | null {
    return this._concept || null;
  }

  /**
   * Set the concept
   */
  set concept(concept: ?ModularUIModel) {
    this._concept = concept instanceof ConceptDetailModel ? concept : null;
  }

  /**
   * Get level of option, used in tree view choice attributes (taxonomy)
   */
  get level(): number {
    return this._level;
  }

  /**
   * Set level of option
   */
  set level(level: number) {
    this._level = level;
  }

  /**
   */
  get isBooleanType(): boolean {
    return this.getContribution("isBooleanType", false);
  }

  /**
   */
  get hasAlternativeLabel(): boolean {
    return this.getContribution("hasAlternativeLabel", false);
  }

  /**
   */
  mergeOption(withOption: ChoiceAttributeOptionModel) {
    this.concept = withOption.concept;

    if (this.children != null && withOption.children != null) {
      this.children.mergeOptions(withOption.children);
    }
  }

  /**
   * Indicates if content comes from the data service
   */
  get hasContentFromData(): boolean {
    return hasAllContentInData();
  }

  /**
   */
  get content(): AttributeContent {
    return this._content;
  }

  /**
   */
  get isUser(): boolean {
    return this._isUser;
  }
}

export default ChoiceAttributeOptionModel;
